// utils/lookupScope.js
//
// The cards a card-lookup element may POINT AT (`options.lookupCardId`),
// decided by who owns the definition the element lives on — a card or an
// element set. Mirrors keepr-api utils/lookupScope.js, which enforces it
// (400 `lookup_out_of_scope`); this is the picker being honest about it.
//
//   collection  that collection's cards: its members (embedded cards[] refs +
//               the cards it owns) and their descendants, behind the
//               ownership boundary (another collection's card, or an
//               unassigned one, is never in scope).
//               PLUS a card OWNED by a published GLOBAL collection
//               (`globalCollectionIds`, the ids the caller's Discover listing
//               reports as published) — keepr-api's branch of the same rule
//               (docs/plans/personal-discover-global.md §4.5, ruling e07).
//   global      the card ITSELF, and nothing else (KPR-237 G-C2). A global
//               card points at no other card: a lookup to one is what a
//               person must map, so it reaches people through a listing.
//               The API's gate refuses any other target (400
//               `global_points_elsewhere`). A card not yet saved has no id,
//               so nothing is offered until it is. Legacy unscoped docs read
//               as global. `user` keeps the older rule: global cards.
//
// CARD BUNDLES are the next scope: a bundled card will look up the other
// cards of its bundle. That is one more branch in `lookupScopeCardIds`; the
// pickers only ever ask this module.

import { normalizeId } from './ids';
import { getCardScope, canAddToCollection } from './cardScopes';
import { getCollectionMemberCardIds } from './collections';
import { borrowedFromOf, fromLabel } from './linkedRecords';

const isGlobalish = card => {
	const scope = getCardScope(card);
	return scope === 'global' || scope === 'legacy';
};

/**
 * @param {object} owner
 * @param {'global'|'collection'|'user'|'legacy'|null} owner.scope the owning definition's scope
 * @param {object} [owner.collection] the owning collection record (its cards[] refs)
 * @param {string} [owner.collectionId] the owning collection's id, when the record is not loaded (yet)
 * @param {string} [owner.ownerCardId] a global definition's own card id: the only target it may have
 * @param {object[]} cards every card definition the caller can read
 * @returns {Set<string>} the ids in scope
 */
export function lookupScopeCardIds({ scope, collection, collectionId, globalCollectionIds = null, ownerCardId = null } = {}, cards = []) {
	const list = Array.isArray(cards) ? cards : [];
	const out = new Set();

	if (scope === 'global' || scope === 'legacy') {
		const self = normalizeId(ownerCardId);
		if (self) out.add(self);
		return out;
	}

	if (scope !== 'collection') {
		list.forEach(card => {
			const id = normalizeId(card?._id);
			if (id && isGlobalish(card)) out.add(id);
		});
		return out;
	}

	const ownerId = normalizeId(collection?._id) || normalizeId(collectionId);
	if (!ownerId) return out;
	const memberIds = getCollectionMemberCardIds(collection || { _id: ownerId }, list);

	const byId = new Map();
	list.forEach(card => {
		const id = normalizeId(card?._id);
		if (id) byId.set(id, card);
	});
	// A member, or a descendant of one — the API's ancestor walk.
	const reachesMember = (card) => {
		const seen = new Set();
		let current = card;
		while (current) {
			const id = normalizeId(current?._id);
			if (!id || seen.has(id)) return false;
			if (memberIds.has(id)) return true;
			seen.add(id);
			current = byId.get(normalizeId(current?.parentCardId));
		}
		return false;
	};

	list.forEach(card => {
		const id = normalizeId(card?._id);
		if (id && canAddToCollection(card, ownerId) && reachesMember(card)) out.add(id);
	});
	// A card another collection OWNS is in scope when that collection is a
	// published global collection.
	if (globalCollectionIds && globalCollectionIds.size) {
		list.forEach(card => {
			const id = normalizeId(card?._id);
			const owner = normalizeId(card?.collection_id);
			if (id && getCardScope(card) === 'collection' && owner && owner !== ownerId && globalCollectionIds.has(owner)) out.add(id);
		});
	}
	return out;
}

/**
 * The card list a lookup picker offers: the scope, plus any card an element
 * ALREADY points at (`keepIds`). A stored out-of-scope target predates the
 * rule; it stays visible and selected so the form shows what is saved rather
 * than a bare id, and the API leaves it alone until it is changed.
 */
export function lookupScopeCards(owner, cards = [], keepIds = []) {
	const ids = lookupScopeCardIds(owner, cards);
	const keep = new Set((keepIds || []).map(normalizeId).filter(Boolean));
	return (Array.isArray(cards) ? cards : []).filter((card) => {
		const id = normalizeId(card?._id);
		return !!id && (ids.has(id) || keep.has(id));
	});
}

/** Every lookup target a list of elements already names. */
export const lookupTargetIds = elements => (Array.isArray(elements) ? elements : [])
	.filter(el => el?.dataType === 'card-lookup')
	.map(el => normalizeId(el?.options?.lookupCardId))
	.filter(Boolean);

/**
 * THE LINKED GROUP of a lookup picker (keepr-api § Lookups across a link):
 * the cards a live incoming link lends this collection — `borrowedCards`
 * from `…/cards?includeLinked=1` — which a collection definition may point a
 * lookup at, labelled "Visit · from Clinic". The API decides the same
 * question (`lookup_out_of_scope` otherwise); a target saved while a link
 * stood and kept after it ended is not in this list, and stays flagged like
 * any other out-of-scope target.
 *
 *   → { label: 'Linked', options: [{ value, label }] } | null
 *
 * `labelFor(card, from)` renders each option's label instead (the pickers
 * pass the qualified card badge — the lender's tile and name, then the card);
 * the words stay on as the option's `searchText`, so the typeahead still
 * finds "clinic".
 */
export function linkedLookupGroup(borrowedCards = [], { labelFor = null } = {}) {
	const options = (Array.isArray(borrowedCards) ? borrowedCards : [])
		.map(card => {
			const value = normalizeId(card?._id);
			const from = borrowedFromOf(card);
			if (!value || !from) return null;
			return { value, label: `${card?.name || 'Card'} · ${fromLabel(from.name)}`, sourceName: from.name, card, from };
		})
		.filter(Boolean)
		.sort((a, b) => a.sourceName.localeCompare(b.sourceName) || a.label.localeCompare(b.label))
		.map(({ sourceName, card, from, ...opt }) => (typeof labelFor === 'function'
			? { ...opt, label: labelFor(card, from), searchText: opt.label }
			: opt));
	return options.length ? { label: 'Linked', options } : null;
}

/**
 * Does this collection hold the card, so that a layout block or a
 * create-item response naming it is worth offering (KPR-237 G12/G13)? A
 * global card another global card names by id is only a door where the
 * collection has it: a member, or a card under one. Anything else stays out.
 */
export function collectionOffersCard(collection, cardId, cards = []) {
	const id = normalizeId(cardId);
	if (!id) return false;
	return lookupScopeCardIds({ scope: 'collection', collection }, cards).has(id);
}
